import { DAY_NIGHT } from './day-night-config.js';

/**
 * The light mask is a two-channel image the night compositor samples per pixel:
 *
 *   red   = light reaching this pixel (lamps, sconces, window shafts)
 *   green = how dark the pixel sits *before* any light, whatever the hour
 *
 * The shader takes `max(nightStrength, green)` as the pixel's night strength and
 * then lets red pull it back toward daylight. That is what makes an interior a
 * lighting environment rather than an exemption from night: a sealed room holds
 * green = 1 and reads dark at noon, a window carves it back only while the sun
 * is up, and a lamp carves it back at any hour.
 *
 * Lights are therefore painted red-only. The mask accumulates with 'lighter',
 * so a white lamp would pile into the green channel and brighten the very
 * darkness it is supposed to be lifting.
 */

const NEIGHBORS = [[0, -1], [1, 0], [0, 1], [-1, 0]];
const LIGHT_TINT = '255, 0, 0';
const STENCIL_TINT = '255, 255, 255';
const maxLightRadiusCache = new WeakMap();
const lightSpriteCache = new Map();

function clamp01(value) {
  return Math.min(1, Math.max(0, value));
}

export function lightSpecFor(tile, config = DAY_NIGHT) {
  if (!tile) return null;
  const fromConfig = config.lights.byTileKey?.[tile.key];
  const fromTile = tile.light;
  if (!fromTile && !fromConfig) return null;
  return {
    radiusTiles: fromConfig?.radiusTiles ?? fromTile?.radiusTiles ?? config.lights.defaultRadiusTiles,
    intensity: fromConfig?.intensity ?? fromTile?.intensity ?? 1,
    origin: fromConfig?.origin ?? fromTile?.origin ?? { x: 0.5, y: 0.5 },
  };
}

export function maxLightRadiusTiles(config = DAY_NIGHT) {
  const cached = maxLightRadiusCache.get(config);
  if (cached !== undefined) return cached;
  let max = config.lights.defaultRadiusTiles;
  for (const spec of Object.values(config.lights.byTileKey ?? {})) {
    if (spec.radiusTiles > max) max = spec.radiusTiles;
  }
  const windows = config.windows;
  if (windows) {
    for (const spec of Object.values(windows.byTileKey ?? {})) {
      const length = spec.lengthTiles ?? windows.lengthTiles;
      if (length > max) max = length;
    }
  }
  maxLightRadiusCache.set(config, max);
  return max;
}

/**
 * The cells that sit in a building's own darkness: the shell the actor stands
 * in, plus the facade band the renderer projects one cell south of it, so the
 * room and its front wall read as one volume.
 *
 * A roofless room is deliberately absent — it is open to the sky and follows
 * outdoor lighting like any other patch of ground.
 */
export function collectInteriorShadeCells(renderer, world, actorPosition) {
  const cells = new Set();
  for (const key of renderer.interiorCellsFor(world, actorPosition)) {
    cells.add(key);
    const [x, y] = key.split(',').map(Number);
    if (renderer.hasSouthFacade(world, x, y)) cells.add(`${x},${y + 1}`);
  }
  return cells;
}

/** Enclosed footprint rather than shell wall. A door leaves its own cell open. */
function isInsideShell(world, x, y) {
  const exterior = world.tileCatalog.get(world.getLayer(x, y, 'exterior'));
  if (!exterior?.shell) return false;
  return !world.isExteriorBoundary(x, y);
}

function isRoomCell(world, x, y) {
  // The `wall` channel is the partition case. Placement rules only allow
  // interior walls on the shell's own boundary today, so this divides nothing
  // yet, but it is what will contain light once they can sit mid-room.
  return isInsideShell(world, x, y) && !world.getLayer(x, y, 'wall');
}

/**
 * One room: everything reachable from (x, y) without crossing an interior wall
 * or leaving the shell. This is what contains a lamp's glow, so a bedroom lamp
 * does not light the kitchen or spill onto the lawn.
 *
 * Returns null when (x, y) is not enclosed at all — callers read that as "this
 * is an outdoor source". Pass `cache` to share one fill between every source in
 * the same room.
 */
export function roomCellsAt(world, x, y, cache = null) {
  if (!isRoomCell(world, x, y)) return null;
  const start = `${x},${y}`;
  const cached = cache?.get(start);
  if (cached) return cached;
  const cells = new Set([start]);
  const pending = [[x, y]];
  while (pending.length) {
    const [cx, cy] = pending.pop();
    for (const [dx, dy] of NEIGHBORS) {
      const nx = cx + dx;
      const ny = cy + dy;
      const key = `${nx},${ny}`;
      if (cells.has(key) || !isRoomCell(world, nx, ny)) continue;
      cells.add(key);
      pending.push([nx, ny]);
    }
  }
  if (cache) for (const key of cells) cache.set(key, cells);
  return cells;
}

/** Whether the frame needs the mask at all — interiors are dark in daylight. */
export function needsLighting(renderer, world, actorPosition, nightStrength) {
  if (nightStrength > 0) return true;
  return renderer.interiorCellsFor(world, actorPosition).size > 0;
}

export function collectLights(world, tiles, bounds, config = DAY_NIGHT) {
  const lights = [];
  const seen = new Set();
  const windowKeys = config.windows?.byTileKey ?? {};
  const channels = config.lights.channels ?? ['onGround'];
  for (let y = bounds.minY; y <= bounds.maxY; y++) {
    for (let x = bounds.minX; x <= bounds.maxX; x++) {
      for (const channel of channels) {
        const tileId = world.getLayer(x, y, channel);
        if (!tileId) continue;
        const tile = tiles.get(tileId);
        // Windows are gathered as directional shafts, not as radial lamps.
        if (tile?.key && windowKeys[tile.key]) continue;
        const spec = lightSpecFor(tile, config);
        if (!spec) continue;
        const placementId = world.getPlacement(x, y, channel);
        if (placementId) {
          if (seen.has(placementId)) continue;
          seen.add(placementId);
        }
        const record = placementId ? world.getPlacementRecord(placementId) : null;
        const originX = record?.x ?? x;
        const originY = record?.y ?? y;
        const lightX = originX + spec.origin.x;
        const lightY = originY + spec.origin.y;
        lights.push({
          x: lightX,
          y: lightY,
          radiusTiles: spec.radiusTiles,
          intensity: spec.intensity,
          // Classified where the light lands, not where its tile sits: a sconce
          // hangs on the wall but lights the cell below it, and a lamp post's
          // flame floats a cell above its base.
          indoor: isInsideShell(world, Math.floor(lightX), Math.floor(lightY)),
        });
      }
    }
  }
  return lights;
}

/**
 * Windows are daylight openings, not lamps: each throws a shaft into the room
 * behind it, and the shaft fades out as night falls.
 */
export function collectWindowShafts(world, tiles, bounds, nightStrength, config = DAY_NIGHT) {
  const windows = config.windows;
  if (!windows?.byTileKey) return [];
  const daylight = 1 - clamp01(nightStrength);
  if (daylight <= 0) return [];
  const shafts = [];
  for (let y = bounds.minY; y <= bounds.maxY; y++) {
    for (let x = bounds.minX; x <= bounds.maxX; x++) {
      const tileId = world.getLayer(x, y, 'wallObject');
      if (!tileId) continue;
      const tile = tiles.get(tileId);
      const spec = tile?.key ? windows.byTileKey[tile.key] : null;
      if (!spec) continue;
      // The shaft starts inside the room rather than on the wall. A facade wall
      // object is drawn one row south of its own cell; anchoring to the room
      // side sidesteps that projection instead of trying to undo it.
      const inward = NEIGHBORS.find(([dx, dy]) => isRoomCell(world, x + dx, y + dy));
      if (!inward) continue;
      const [dx, dy] = inward;
      shafts.push({
        x: x + dx + 0.5,
        y: y + dy + 0.5,
        dx,
        dy,
        lengthTiles: spec.lengthTiles ?? windows.lengthTiles,
        spreadTiles: spec.spreadTiles ?? windows.spreadTiles,
        mouthRadiusTiles: spec.mouthRadiusTiles ?? windows.mouthRadiusTiles,
        intensity: (spec.intensity ?? windows.intensity) * daylight,
      });
    }
  }
  return shafts;
}

function addSmoothStops(gradient, coreRatio, intensity, tint) {
  const stops = 8;
  for (let i = 1; i <= stops; i++) {
    const t = i / stops;
    const pos = coreRatio + t * (1 - coreRatio);
    const smooth = t * t * (3 - 2 * t);
    gradient.addColorStop(Math.min(1, pos), `rgba(${tint}, ${intensity * (1 - smooth)})`);
  }
}

function lightSprite(tileSize, radiusTiles, coreRadiusTiles, tint) {
  const key = `${tileSize}|${radiusTiles}|${coreRadiusTiles}|${tint}`;
  const cached = lightSpriteCache.get(key);
  if (cached) return cached;
  const radius = radiusTiles * tileSize;
  const size = Math.ceil(radius) * 2;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  const center = size / 2;
  const gradient = ctx.createRadialGradient(center, center, 0, center, center, radius);
  const coreRatio = Math.min(0.999, Math.max(0, (coreRadiusTiles * tileSize) / radius));
  gradient.addColorStop(0, `rgba(${tint}, 1)`);
  if (coreRatio > 0) gradient.addColorStop(coreRatio, `rgba(${tint}, 1)`);
  addSmoothStops(gradient, coreRatio, 1, tint);
  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.arc(center, center, radius, 0, Math.PI * 2);
  ctx.fill();
  lightSpriteCache.set(key, canvas);
  return canvas;
}

function paintRadial({ ctx, origin, tileSize, tint }, x, y, radiusTiles, intensity, coreRadiusTiles) {
  const radius = radiusTiles * tileSize;
  if (radius <= 0 || intensity <= 0) return;
  const sprite = lightSprite(tileSize, radiusTiles, coreRadiusTiles, tint);
  const sx = origin.x + x * tileSize - sprite.width / 2;
  const sy = origin.y + y * tileSize - sprite.height / 2;
  const previousAlpha = ctx.globalAlpha;
  ctx.globalAlpha = intensity;
  ctx.drawImage(sprite, sx, sy);
  ctx.globalAlpha = previousAlpha;
}

function paintLamp(geometry, light) {
  paintRadial(
    geometry, light.x, light.y, light.radiusTiles, light.intensity,
    geometry.config.lights.coreRadiusTiles,
  );
}

function paintShaft(geometry, shaft) {
  const { ctx, origin, tileSize, tint } = geometry;
  if (shaft.intensity <= 0) return;
  const sx = origin.x + shaft.x * tileSize;
  const sy = origin.y + shaft.y * tileSize;
  const length = shaft.lengthTiles * tileSize;
  const ex = sx + shaft.dx * length;
  const ey = sy + shaft.dy * length;
  // Perpendicular to the shaft, so the cone widens as it crosses the room.
  const px = -shaft.dy;
  const py = shaft.dx;
  const near = tileSize / 2;
  const far = (shaft.spreadTiles * tileSize) / 2;

  const gradient = ctx.createLinearGradient(sx, sy, ex, ey);
  gradient.addColorStop(0, `rgba(${tint}, ${shaft.intensity})`);
  addSmoothStops(gradient, 0, shaft.intensity, tint);
  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.moveTo(sx + px * near, sy + py * near);
  ctx.lineTo(ex + px * far, ey + py * far);
  ctx.lineTo(ex - px * far, ey - py * far);
  ctx.lineTo(sx - px * near, sy - py * near);
  ctx.closePath();
  ctx.fill();

  // Without a glow at the opening the cone's apex reads as a hard wedge.
  paintRadial(geometry, shaft.x, shaft.y, shaft.mouthRadiusTiles, shaft.intensity, 0);
}

function cellPath(path, cells, origin, tileSize) {
  for (const key of cells) {
    const [x, y] = key.split(',').map(Number);
    path.rect(origin.x + x * tileSize, origin.y + y * tileSize, tileSize, tileSize);
  }
}

/**
 * Clip a source to what it is allowed to light, then paint it.
 *
 * An indoor source is clipped to its own room. An outdoor source is clipped to
 * everything *except* the revealed interior, so a street lamp cannot shine
 * through an exterior wall. An indoor source in a building the player is not
 * standing in is skipped entirely — its room is under a roof, and lighting it
 * would make the roof glow.
 */
function paintSource(geometry, world, shadeCells, rooms, source, paint) {
  const { ctx, origin, tileSize, width, height } = geometry;
  const cellX = Math.floor(source.x);
  const cellY = Math.floor(source.y);
  const room = roomCellsAt(world, cellX, cellY, rooms);
  if (room && !shadeCells.has(`${cellX},${cellY}`)) return;
  ctx.save();
  const path = new Path2D();
  if (room) {
    cellPath(path, room, origin, tileSize);
    ctx.clip(path);
  } else if (shadeCells.size) {
    path.rect(0, 0, width, height);
    cellPath(path, shadeCells, origin, tileSize);
    ctx.clip(path, 'evenodd');
  }
  paint(geometry, source);
  ctx.restore();
}

function maskBounds(origin, tileSize, width, height, config) {
  const pad = Math.ceil(maxLightRadiusTiles(config)) + 2;
  return {
    minX: Math.floor(-origin.x / tileSize) - pad,
    minY: Math.floor(-origin.y / tileSize) - pad,
    maxX: Math.ceil((width - origin.x) / tileSize) + pad,
    maxY: Math.ceil((height - origin.y) / tileSize) + pad,
  };
}

function paintAllSources(geometry, world, renderer, shadeCells, bounds, nightStrength) {
  const { config } = geometry;
  const rooms = new Map();
  for (const light of collectLights(world, renderer.tiles, bounds, config)) {
    paintSource(geometry, world, shadeCells, rooms, light, paintLamp);
  }
  for (const shaft of collectWindowShafts(world, renderer.tiles, bounds, nightStrength, config)) {
    paintSource(geometry, world, shadeCells, rooms, shaft, paintShaft);
  }
}

export function drawLightMask(ctx, {
  renderer, world, origin, tileSize, width, height, actorPosition,
  nightStrength = 1, config = DAY_NIGHT,
}) {
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.globalCompositeOperation = 'source-over';
  ctx.globalAlpha = 1;
  ctx.clearRect(0, 0, width, height);
  ctx.fillStyle = '#000000';
  ctx.fillRect(0, 0, width, height);

  const bounds = maskBounds(origin, tileSize, width, height, config);
  const shadeCells = collectInteriorShadeCells(renderer, world, actorPosition);

  ctx.globalCompositeOperation = 'lighter';

  const shadeLevel = Math.round(clamp01(config.interiorShade) * 255);
  if (shadeLevel > 0) {
    ctx.fillStyle = `rgb(0, ${shadeLevel}, 0)`;
    for (const key of shadeCells) {
      const [x, y] = key.split(',').map(Number);
      ctx.fillRect(origin.x + x * tileSize, origin.y + y * tileSize, tileSize, tileSize);
    }
  }

  const geometry = { ctx, origin, tileSize, width, height, config, tint: LIGHT_TINT };
  paintAllSources(geometry, world, renderer, shadeCells, bounds, nightStrength);

  ctx.globalCompositeOperation = 'source-over';
}

/**
 * Canvas-2D fallback stencil: one canvas whose *alpha* is the finished
 * darkness. The WebGL path samples the mask's two channels and combines them in
 * the shader, but 2D compositing cannot isolate a channel, so the fallback
 * needs the same geometry baked into an alpha it can use as a stencil.
 *
 * Interior cells are cleared before being refilled so they land on exactly
 * `interiorShade` instead of accumulating on top of the night level.
 */
export function drawDarknessStencil(ctx, {
  renderer, world, origin, tileSize, width, height, actorPosition,
  nightStrength = 1, config = DAY_NIGHT,
}) {
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.globalCompositeOperation = 'source-over';
  ctx.globalAlpha = 1;
  ctx.clearRect(0, 0, width, height);

  const bounds = maskBounds(origin, tileSize, width, height, config);
  const shadeCells = collectInteriorShadeCells(renderer, world, actorPosition);

  const night = clamp01(nightStrength);
  if (night > 0) {
    ctx.fillStyle = `rgba(${STENCIL_TINT}, ${night})`;
    ctx.fillRect(0, 0, width, height);
  }

  const shade = clamp01(config.interiorShade);
  if (shadeCells.size) {
    for (const pass of ['destination-out', 'source-over']) {
      ctx.globalCompositeOperation = pass;
      ctx.fillStyle = pass === 'destination-out' ? '#ffffff' : `rgba(${STENCIL_TINT}, ${shade})`;
      for (const key of shadeCells) {
        const [x, y] = key.split(',').map(Number);
        ctx.fillRect(origin.x + x * tileSize, origin.y + y * tileSize, tileSize, tileSize);
      }
    }
  }

  // Light removes darkness here rather than adding brightness.
  ctx.globalCompositeOperation = 'destination-out';
  const geometry = { ctx, origin, tileSize, width, height, config, tint: STENCIL_TINT };
  paintAllSources(geometry, world, renderer, shadeCells, bounds, nightStrength);

  ctx.globalCompositeOperation = 'source-over';
}
